import React, { useEffect, useMemo, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { getAuth } from "firebase/auth";
import { ymd } from "../firebase";
import {
  startCheckoutStandard,
  startCheckoutOvernight,
  checkAvailability as rpcCheckAvailability,
  checkAvailabilityRange as rpcCheckAvailabilityRange,
  openStripeCheckout,
} from "../lib/stripeRpc";

/** =============================== Config =============================== */
// Guest fee baked into guest-facing price (must match Explore.js / ListingDetail.js)
const FEE_PCT = 15;

// Fallback tax rate used ONLY for client-side estimates.
// The server/Stripe is authoritative for the final amount.
const DEFAULT_TAX_RATE_PCT = 10;

/** =============================== Helpers ============================== */
const currency = (n) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
  }).format(n ?? 0);

function cents(n) {
  return Math.max(0, Math.round(Number(n || 0)));
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function toYYYYMMDD(d) {
  // Prefer shared helper; safe fallback on error
  try {
    return ymd(d);
  } catch {
    const dt = new Date(d);
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${dt.getFullYear()}-${m}-${day}`;
  }
}

function parseYYYYMMDD(s) {
  const [Y, M, D] = String(s || "").split("-").map(Number);
  if (!Y || !M || !D) return null;
  return new Date(Y, M - 1, D);
}

function daysBetween(startStr, endStr) {
  const s = parseYYYYMMDD(startStr);
  const e = parseYYYYMMDD(endStr);
  if (!s || !e) return 0;
  const diff =
    Math.round((e.setHours(0, 0, 0, 0) - s.setHours(0, 0, 0, 0)) / 86400000);
  return Math.max(0, diff);
}

function fmtHour(h) {
  const n = Number(h);
  if (!Number.isFinite(n) || n < 0 || n > 23) return "—";
  const ampm = n >= 12 ? "PM" : "AM";
  const hour = n % 12 || 12;
  return `${hour} ${ampm}`;
}

// Normalize pricing to cents (accepts either ...Cents or dollars fields)
function normalizePriceCents(centsField, dollarsField) {
  const c = Number(centsField);
  if (Number.isFinite(c) && c > 0) return Math.round(c);
  const d = Number(dollarsField);
  if (Number.isFinite(d) && d > 0) return Math.round(d * 100);
  return 0;
}

// Same guest-price logic as ListingDetail / Explore
function toGuestPrice(baseDollars, precomputed) {
  const preset = Number(precomputed);
  if (Number.isFinite(preset) && preset > 0) return Math.round(preset);
  const b = Number(baseDollars);
  if (!Number.isFinite(b) || b <= 0) return null;
  return Math.round(b * (1 + FEE_PCT / 100));
}

function buildReturnToURL() {
  try {
    const url = new URL(window.location.href);
    return url.pathname + (url.search || "");
  } catch {
    return "/checkout";
  }
}

/** ============================== Component ============================= */
export default function Checkout({ user, requireAuth }) {
  const { id } = useParams(); // listingId in route: /checkout/:id
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const { state } = useLocation(); // may contain { listing }
  const auth = getAuth();

  /** ----------------------------- Listing load ----------------------------- */
  const [listing, setListing] = useState(state?.listing || null);
  const [loading, setLoading] = useState(!state?.listing);
  const [error, setError] = useState("");

  // Query inference (canonical URL semantics)
  const hasDateQ = Boolean(search.get("date"));
  const hasRangeQ = Boolean(search.get("start")) && Boolean(search.get("end"));
  const initialModeQuery =
    hasRangeQ ? "overnight" : hasDateQ ? "day" : (search.get("mode") || "").toLowerCase();

  const [mode, setMode] = useState(
    initialModeQuery === "overnight" ? "overnight" : "day"
  );

  // Day inputs
  const [date, setDate] = useState(() => search.get("date") || "");

  // Overnight inputs (contiguous range; end is check-out / exclusive)
  const [startDate, setStartDate] = useState(() => search.get("start") || "");
  const [endDate, setEndDate] = useState(() => search.get("end") || "");
  const nights = useMemo(
    () => daysBetween(startDate, endDate),
    [startDate, endDate]
  );

  // Shared inputs
  const [guests, setGuests] = useState(() => Number(search.get("guests") || 1));
  const [note, setNote] = useState("");

  // Availability preflight UX (client hint; server remains source of truth)
  const [availState, setAvailState] = useState("idle"); // idle|checking|available|unavailable
  const [availMsg, setAvailMsg] = useState("");

  // Submission state
  const [checking, setChecking] = useState(false);

  // Mobile-only: sticky bar + keyboard avoidance
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  // Derived dates
  const minDate = useMemo(() => toYYYYMMDD(new Date()), []);
  const minEndForStart = useMemo(
    () =>
      startDate
        ? toYYYYMMDD(addDays(parseYYYYMMDD(startDate), 1))
        : minDate,
    [startDate, minDate]
  );

  // Keep URL in sync for shareability/deeplinks
  const syncSearch = (patch) => {
    const next = new URLSearchParams(search);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") next.delete(k);
      else next.set(k, String(v));
    });
    setSearch(next, { replace: true });
  };

  // Load listing if not provided in state
  useEffect(() => {
    let alive = true;
    (async () => {
      if (listing) {
        // Backfill mode from listing capabilities only when query didn’t specify
        const lm = (listing.bookingMode || "day").toLowerCase();
        if (!hasDateQ && !hasRangeQ) {
          if (initialModeQuery && (lm === "hybrid" || lm === initialModeQuery)) {
            setMode(initialModeQuery);
          } else if (lm === "overnight") {
            setMode("overnight");
          } else {
            setMode("day");
          }
        }
        return;
      }
      try {
        setLoading(true);
        const mod = await import("../firebase");
        const getListingById =
          mod.getListingById || mod.fetchListing || mod.getListing;
        if (!getListingById) {
          setError(
            "Listing loader not implemented. Pass listing via state or add getListingById() in firebase.js."
          );
          return;
        }
        const doc = await getListingById(id);
        if (!alive) return;
        if (!doc) {
          setError("Listing not found.");
        }
        setListing(doc || null);
        if (doc) {
          const lm = (doc.bookingMode || "day").toLowerCase();
          if (!hasDateQ && !hasRangeQ) {
            if (initialModeQuery && (lm === "hybrid" || lm === initialModeQuery)) {
              setMode(initialModeQuery);
            } else if (lm === "overnight") {
              setMode("overnight");
            } else {
              setMode("day");
            }
          }
        }
      } catch (e) {
        console.error(e);
        if (alive) setError("Couldn’t load this listing.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /** --------------------------- Pricing model --------------------------- */
  const bookingMode = (listing?.bookingMode || "day").toLowerCase(); // "day"|"overnight"|"hybrid"

  // Host base prices (cents) from Firestore
  const dayRateHostCents = normalizePriceCents(
    listing?.dayPriceCents,
    listing?.dayPrice ?? listing?.dayRate
  );
  const nightlyRateHostCents = normalizePriceCents(
    listing?.nightlyPriceCents,
    listing?.nightlyPrice
  );

  // Host prices in dollars
  const baseDayPrice = dayRateHostCents > 0 ? dayRateHostCents / 100 : null;
  const baseNightPrice = nightlyRateHostCents > 0 ? nightlyRateHostCents / 100 : null;

  // Guest-facing prices: host + 15% fee (or precomputed guestDayPrice / guestNightlyPrice)
  const guestDayPrice = toGuestPrice(baseDayPrice, listing?.guestDayPrice);
  const guestNightlyPrice = toGuestPrice(baseNightPrice, listing?.guestNightlyPrice);

  const hasGuestDayPrice =
    Number.isFinite(guestDayPrice) && Number(guestDayPrice) > 0;
  const hasGuestNightlyPrice =
    (bookingMode === "overnight" || bookingMode === "hybrid") &&
    Number.isFinite(guestNightlyPrice) &&
    Number(guestNightlyPrice) > 0;

  // Guest prices in cents for arithmetic
  const guestDayRateCents = hasGuestDayPrice ? guestDayPrice * 100 : 0;
  const guestNightlyRateCents = hasGuestNightlyPrice ? guestNightlyPrice * 100 : 0;

  const minNights = Number.isFinite(Number(listing?.minNights))
    ? Number(listing?.minNights)
    : 1;
  const maxNights =
    Number.isFinite(Number(listing?.maxNights)) && Number(listing?.maxNights) > 0
      ? Number(listing?.maxNights)
      : 30;
  const checkInHour = Number.isFinite(Number(listing?.checkInHour))
    ? Number(listing?.checkInHour)
    : null;
  const checkOutHour = Number.isFinite(Number(listing?.checkOutHour))
    ? Number(listing?.checkOutHour)
    : null;
  const capacity = Number.isFinite(Number(listing?.capacity))
    ? Number(listing?.capacity)
    : 99;

  // Active unit price (guest-facing) for totals and display
  const activeUnitRateCents =
    mode === "overnight"
      ? guestNightlyRateCents || guestDayRateCents
      : guestDayRateCents || guestNightlyRateCents;

  // How many units are being booked?
  const estQuantity =
    mode === "overnight" ? (nights > 0 ? nights : 0) : date ? 1 : 0;

  // Client-side display-only subtotal (server is authoritative)
  const estSubtotalCents = useMemo(() => {
    if (!activeUnitRateCents || estQuantity <= 0) return 0;
    return activeUnitRateCents * estQuantity;
  }, [activeUnitRateCents, estQuantity]);

  // Tax rate: allow per-listing overrides, else fallback
  const taxRatePct = (() => {
    const raw =
      listing?.taxRatePct ??
      listing?.taxRatePercent ??
      listing?.taxRate ??
      listing?.occupancyTaxPct ??
      listing?.occupancyTaxPercent;
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 0 && n <= 50) return n;
    return DEFAULT_TAX_RATE_PCT;
  })();

  const estTaxCents = useMemo(
    () => Math.round(estSubtotalCents * (taxRatePct / 100)),
    [estSubtotalCents, taxRatePct]
  );

  // Total = guest-facing price + estimated tax (ONE price guests see here)
  const estTotalCents = estSubtotalCents + estTaxCents;

  /** ----------------------------- Validation ---------------------------- */
  const supportsOvernight =
    bookingMode === "overnight" || bookingMode === "hybrid";
  const supportsDay = bookingMode === "day" || bookingMode === "hybrid";

  const canConfirmDay =
    Boolean(listing) &&
    Boolean(date) &&
    guests >= 1 &&
    guestDayRateCents > 0;

  const nightsOk =
    nights > 0 &&
    nights >= minNights &&
    nights <= maxNights &&
    guestNightlyRateCents > 0;

  const canConfirmOvernight =
    Boolean(listing) &&
    guests >= 1 &&
    Boolean(startDate) &&
    Boolean(endDate) &&
    nightsOk;

  const canConfirmBase = mode === "overnight" ? canConfirmOvernight : canConfirmDay;
  // Also disable if preflight says unavailable
  const canConfirm = canConfirmBase && (availState !== "unavailable");

  /** ---------------------- Availability preflight (UX) --------------------- */
  useEffect(() => {
    let cancelled = false;

    const shouldCheckDay = supportsDay && mode === "day" && date;
    const shouldCheckRange =
      supportsOvernight && mode === "overnight" && startDate && endDate && nights > 0;

    async function run() {
      if (!listing?.id) return;
      if (!shouldCheckDay && !shouldCheckRange) {
        setAvailState("idle");
        setAvailMsg("");
        return;
      }
      try {
        setAvailState("checking");
        setAvailMsg("");

        if (shouldCheckDay) {
          const out = await rpcCheckAvailability({
            listingId: listing.id,
            date,
          });
          if (cancelled) return;
          if (out?.ok && out?.available) {
            setAvailState("available");
            setAvailMsg("");
          } else {
            setAvailState("unavailable");
            setAvailMsg("That date is unavailable—please choose another.");
          }
          return;
        }

        if (shouldCheckRange) {
          const out = await rpcCheckAvailabilityRange({
            listingId: listing.id,
            startDate,
            endDateExclusive: endDate, // server expects exclusive end
          });
          if (cancelled) return;
          if (out?.ok && out?.available) {
            setAvailState("available");
            setAvailMsg("");
          } else {
            setAvailState("unavailable");
            setAvailMsg(
              out?.firstUnavailable
                ? `Unavailable on ${out.firstUnavailable}—adjust your dates.`
                : "Those dates are unavailable—please choose another range."
            );
          }
        }
      } catch {
        // Network/auth error shouldn’t hard-block checkout; allow server to decide later
        if (cancelled) return;
        setAvailState("idle");
        setAvailMsg("");
      }
    }

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing?.id, mode, date, startDate, endDate, nights, supportsDay, supportsOvernight]);

  /** ---------------------------- Confirm & Pay ---------------------------- */
  const confirmBooking = async () => {
    if (!canConfirm || checking) return;

    const me = auth.currentUser || user;
    if (!me) {
      // Auth gate with returnTo
      const returnTo = buildReturnToURL();
      if (typeof requireAuth === "function") {
        try {
          requireAuth("signin", { returnTo });
        } catch {
          // no-op; fallback to navigate
          navigate(`/account?returnTo=${encodeURIComponent(returnTo)}`);
        }
      } else {
        navigate(`/account?returnTo=${encodeURIComponent(returnTo)}`);
      }
      return;
    }

    setChecking(true);
    setError("");

    try {
      const base = {
        listingId: listing?.id || id,
        guests,
        note: note.trim(),
      };

      const out =
        mode === "overnight"
          ? await startCheckoutOvernight({
              ...base,
              startDate,                 // YYYY-MM-DD check-in
              endDateExclusive: endDate, // YYYY-MM-DD check-out (exclusive)
            })
          : await startCheckoutStandard({
              ...base,
              date, // YYYY-MM-DD (day booking)
            });

      if (out?.sessionUrl) {
        openStripeCheckout(out.sessionUrl);
        return;
      }

      if (out?.error) throw new Error(out.error);
      throw new Error("Missing sessionUrl from Stripe RPC response.");
    } catch (e) {
      console.error(e);
      setError(
        e?.message ||
          "We couldn't start payment. If dates were just taken, please pick new ones."
      );
      setChecking(false);
    }
  };

  /** ------------------------- Keyboard-aware mobile ------------------------ */
  useEffect(() => {
    // Heuristic: when an input/textarea focuses or visual viewport shrinks, hide sticky bar
    const onFocusIn = (e) => {
      const t = e.target?.tagName?.toLowerCase();
      if (t === "input" || t === "textarea" || t === "select") setIsKeyboardOpen(true);
    };
    const onFocusOut = () => setTimeout(() => setIsKeyboardOpen(false), 50);

    window.addEventListener("focusin", onFocusIn);
    window.addEventListener("focusout", onFocusOut);

    const vv = window.visualViewport;
    const onVV = () => {
      if (!vv) return;
      // If viewport height reduced substantially, likely keyboard open
      const ratio = vv.height / window.innerHeight;
      setIsKeyboardOpen(ratio < 0.8);
    };
    if (vv) vv.addEventListener("resize", onVV);

    return () => {
      window.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("focusout", onFocusOut);
      if (vv) vv.removeEventListener("resize", onVV);
    };
  }, []);

  /** ------------------------------ Early states ---------------------------- */
  if (loading) {
    return (
      <main className="mx-auto max-w-5xl px-4 py-10">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 rounded bg-neutral-200" />
          <div className="h-40 rounded-xl bg-neutral-200" />
        </div>
      </main>
    );
  }

  if (error && !listing) {
    return (
      <main className="mx-auto max-w-xl px-4 py-16">
        <div className="rounded-2xl border border-neutral-200 bg-white p-6 text-center shadow-sm">
          <h1 className="text-xl font-semibold">Checkout</h1>
          <p className="mt-2 text-neutral-600">{error}</p>
          <div className="mt-4 flex justify-center gap-3">
            <Link
              to="/explore"
              className="rounded-full border border-neutral-300 px-4 py-2 text-sm hover:border-[#D4AF37]"
            >
              Back to Explore
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const cityState = [listing?.city, listing?.stateCode ?? listing?.state]
    .filter(Boolean)
    .join(", ");

  /** ================================= View ================================ */
  return (
    <main
      className={
        // Extra bottom padding so content never hides behind the sticky bar
        "mx-auto max-w-5xl px-4 pb-28 pt-4 sm:pb-8 sm:pt-8"
      }
    >
      {/* Breadcrumb / Back */}
      <div className="mb-3 flex items-center justify-between sm:mb-4">
        <div className="text-sm text-neutral-600">
          <Link to="/" className="hover:underline">
            Home
          </Link>{" "}
          <span aria-hidden>›</span>{" "}
          <Link to="/explore" className="hover:underline">
            Explore
          </Link>{" "}
          <span aria-hidden>›</span>{" "}
          <span className="text-neutral-900">Checkout</span>
        </div>
        <Link
          to={`/listing/${listing.id}`}
          state={{ listing }}
          className="hidden rounded-full border border-neutral-300 px-3 py-1.5 text-sm hover:border-neutral-400 sm:inline"
        >
          Back to listing
        </Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_420px] lg:gap-8">
        {/* Left: booking form */}
        <div className="space-y-6">
          <section className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold">Your booking</h2>

              {/* Mode selector if hybrid */}
              {(listing?.bookingMode || "").toLowerCase() === "hybrid" && (
                <div
                  role="tablist"
                  aria-label="Booking mode"
                  className="inline-flex rounded-full border border-neutral-300 p-1 text-sm"
                >
                  <button
                    role="tab"
                    className={`rounded-full px-4 py-1.5 ${
                      mode === "day" ? "bg-black text-white" : "hover:bg-neutral-100"
                    }`}
                    onClick={() => {
                      setMode("day");
                      setAvailState("idle");
                      setAvailMsg("");
                      syncSearch({ mode: "day", date, start: "", end: "" });
                    }}
                    aria-selected={mode === "day"}
                  >
                    Day
                  </button>
                  <button
                    role="tab"
                    className={`rounded-full px-4 py-1.5 ${
                      mode === "overnight" ? "bg-black text-white" : "hover:bg-neutral-100"
                    }`}
                    onClick={() => {
                      setMode("overnight");
                      setAvailState("idle");
                      setAvailMsg("");
                      syncSearch({
                        mode: "overnight",
                        start: startDate,
                        end: endDate,
                        date: "",
                      });
                    }}
                    aria-selected={mode === "overnight"}
                  >
                    Stay
                  </button>
                </div>
              )}
            </div>
            <div className="mx-auto mt-2 h-0.5 w-12 rounded-full bg-[#D4AF37]" />

            {/* Daytime form */}
            {supportsDay && mode === "day" && (
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label className="text-sm">
                  Date
                  <input
                    type="date"
                    min={minDate}
                    value={date}
                    onChange={(e) => {
                      setDate(e.target.value);
                      setAvailState("idle");
                      setAvailMsg("");
                      syncSearch({ date: e.target.value, mode: "day", start: "", end: "" });
                    }}
                    className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                  />
                </label>
                <label className="text-sm">
                  Guests (max {capacity})
                  <input
                    type="number"
                    min={1}
                    max={capacity}
                    value={guests}
                    onChange={(e) => {
                      const v = Math.max(1, Math.min(capacity, Number(e.target.value || 1)));
                      setGuests(v);
                      syncSearch({ guests: v });
                    }}
                    className="mt-1 w-40 rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                  />
                </label>

                {availState === "unavailable" && (
                  <div className="sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {availMsg || "That date is unavailable—please choose another."}
                  </div>
                )}
              </div>
            )}

            {/* Overnight form */}
            {supportsOvernight && mode === "overnight" && (
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label className="text-sm">
                  Check-in
                  <input
                    type="date"
                    min={minDate}
                    value={startDate}
                    onChange={(e) => {
                      const v = e.target.value;
                      setStartDate(v);
                      setAvailState("idle");
                      setAvailMsg("");
                      syncSearch({ start: v, mode: "overnight", date: "" });
                      // Ensure endDate is always > startDate
                      if (endDate && parseYYYYMMDD(endDate) <= parseYYYYMMDD(v)) {
                        const next = toYYYYMMDD(addDays(parseYYYYMMDD(v), 1));
                        setEndDate(next);
                        syncSearch({ end: next });
                      }
                    }}
                    className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                  />
                </label>
                <label className="text-sm">
                  Check-out
                  <input
                    type="date"
                    min={minEndForStart}
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setAvailState("idle");
                      setAvailMsg("");
                      syncSearch({ end: e.target.value });
                    }}
                    className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                  />
                </label>

                <div className="sm:col-span-2 grid grid-cols-2 gap-4">
                  <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span>Nights</span>
                      <span className="font-medium">{nights || "—"}</span>
                    </div>
                    <div className="mt-1 text-xs text-neutral-600">
                      Min {minNights}, max {maxNights}.
                    </div>
                  </div>
                  <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span>Check-in / out</span>
                      <span className="font-medium">
                        {fmtHour(checkInHour)} → {fmtHour(checkOutHour)}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-neutral-600">Set by host</div>
                  </div>
                </div>

                <label className="sm:col-span-2 text-sm">
                  Guests (max {capacity})
                  <input
                    type="number"
                    min={1}
                    max={capacity}
                    value={guests}
                    onChange={(e) => {
                      const v = Math.max(1, Math.min(capacity, Number(e.target.value || 1)));
                      setGuests(v);
                      syncSearch({ guests: v });
                    }}
                    className="mt-1 w-40 rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
                  />
                </label>

                {/* Overnight validation hints */}
                <div className="sm:col-span-2 text-xs text-neutral-600">
                  {startDate && endDate && nights > 0 ? (
                    nights < minNights ? (
                      <span>
                        Minimum stay is {minNights} night{minNights === 1 ? "" : "s"}.
                      </span>
                    ) : nights > maxNights ? (
                      <span>Maximum stay is {maxNights} nights.</span>
                    ) : null
                  ) : (
                    <span>Select check-in and check-out dates.</span>
                  )}
                </div>

                {availState === "unavailable" && (
                  <div className="sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {availMsg || "Those dates are unavailable—please choose another range."}
                  </div>
                )}
              </div>
            )}

            <label className="mt-5 block text-sm">
              Notes for host (optional)
              <textarea
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={
                  mode === "overnight"
                    ? "Arrival window, parking needs, special requests…"
                    : "Arrival time, purpose of event, special requests…"
                }
                className="mt-1 w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
              />
            </label>
          </section>

          <section className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
            <h3 className="text-base font-semibold">Cancellation policy</h3>
            <p className="mt-2 text-sm text-neutral-700">
              Full refund up to 7 days before{" "}
              {mode === "overnight" ? "check-in" : "the event day"}. Within 7 days, host
              approval is required for changes or cancellations.
            </p>
          </section>
        </div>

        {/* Right: price summary (desktop/tablet) */}
        <aside className="hidden self-start rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm lg:sticky lg:top-6 lg:block">
          <div className="flex items-start gap-3">
            <div className="h-16 w-16 overflow-hidden rounded-lg border border-neutral-200">
              <img
                src={
                  listing?.photos?.[0] ||
                  "https://images.unsplash.com/photo-1505692794403-34d4982f88aa?q=80&w=800&auto=format&fit=crop"
                }
                alt={listing?.title || "Listing photo"}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">{listing?.title}</div>
              <div className="truncate text-xs text-neutral-600">{cityState}</div>
              <div className="mt-1 text-xs text-neutral-500">
                {mode === "overnight" ? "Overnight stay" : "Day use"}
                {mode === "overnight" &&
                (checkInHour != null || checkOutHour != null)
                  ? ` • ${fmtHour(checkInHour)}–${fmtHour(checkOutHour)}`
                  : ""}
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-xl bg-neutral-50 p-3 text-sm">
            {mode === "overnight" ? (
              <>
                <div className="flex justify-between">
                  <span>Check-in</span>
                  <span className="font-medium">{startDate || "—"}</span>
                </div>
                <div className="mt-1 flex justify-between">
                  <span>Check-out</span>
                  <span className="font-medium">{endDate || "—"}</span>
                </div>
                <div className="mt-1 flex justify-between">
                  <span>Nights</span>
                  <span className="font-medium">{nights || "—"}</span>
                </div>
                <div className="mt-1 flex justify-between">
                  <span>Guests</span>
                  <span className="font-medium">{guests}</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between">
                  <span>Selected date</span>
                  <span className="font-medium">{date || "—"}</span>
                </div>
                <div className="mt-1 flex justify-between">
                  <span>Guests</span>
                  <span className="font-medium">{guests}</span>
                </div>
              </>
            )}
          </div>

          <div className="mt-4 border-t border-neutral-200 pt-4 text-sm">
            {activeUnitRateCents > 0 && estQuantity > 0 ? (
              <div className="mb-1 flex justify-between">
                <span>
                  {currency(activeUnitRateCents / 100)} × {estQuantity}{" "}
                  {mode === "overnight"
                    ? `night${estQuantity === 1 ? "" : "s"}`
                    : `day${estQuantity === 1 ? "" : "s"}`}
                </span>
                <span>{currency(estSubtotalCents / 100)}</span>
              </div>
            ) : (
              <div className="mb-1 flex justify-between">
                <span>Space price</span>
                <span>
                  {activeUnitRateCents > 0 ? currency(activeUnitRateCents / 100) : "—"}
                </span>
              </div>
            )}

            {estTaxCents > 0 && (
              <div className="mb-1 flex justify-between">
                <span>Estimated taxes</span>
                <span>{currency(estTaxCents / 100)}</span>
              </div>
            )}

            <div className="mt-2 flex justify-between text-base font-semibold">
              <span>Total (est.)</span>
              <span>{currency(estTotalCents / 100)}</span>
            </div>

            <p className="mt-1 text-[11px] leading-snug text-neutral-500">
              Total includes an estimated tax amount. The final total is calculated on the
              server and shown on Stripe. If the date(s) are no longer available at payment
              time, you’ll be prompted to pick new ones.
            </p>
          </div>

          {error && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            onClick={confirmBooking}
            disabled={!canConfirm || checking}
            className="mt-4 w-full rounded-full bg-black px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#B8860B] disabled:opacity-60"
          >
            {checking ? "Redirecting…" : "Confirm & Pay"}
          </button>

          <p className="mt-2 text-center text-xs text-neutral-500">
            You’ll be redirected to a secure Stripe checkout. Your booking is confirmed
            once payment completes.
          </p>
        </aside>
      </div>

      {/* Sticky bottom booking bar (mobile) */}
      <div
        className={`fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 sm:hidden transition-transform ${
          isKeyboardOpen ? "translate-y-full" : "translate-y-0"
        }`}
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)",
        }}
      >
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 pt-3">
          <div className="min-w-0 flex-1 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-neutral-600">Total (est.)</span>
              <span className="text-base font-semibold">
                {currency(estTotalCents / 100)}
              </span>
            </div>
            <div className="mt-0.5 truncate text-[11px] text-neutral-500">
              {mode === "overnight"
                ? `${startDate || "—"} → ${endDate || "—"} • ${nights || 0} night${
                    (nights || 0) === 1 ? "" : "s"
                  } • ${guests} guest${guests === 1 ? "" : "s"}`
                : `${date || "—"} • ${guests} guest${
                    guests === 1 ? "" : "s"
                  }`}
            </div>
          </div>

          <button
            onClick={confirmBooking}
            disabled={!canConfirm || checking}
            className="shrink-0 rounded-full bg-black px-5 py-3 text-sm font-semibold text-white hover:bg-[#B8860B] disabled:opacity-60"
          >
            {checking ? "Redirecting…" : "Confirm & Pay"}
          </button>
        </div>
        {error && (
          <div className="mx-auto max-w-5xl px-4 pt-2">
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
